/**
 * RescheduleTicket Component
 *
 * Componente responsável por exibir um modal para reagendamento de tickets/chamados.
 * Permite aos usuários selecionar nova data, horário, setor e usuário responsável
 * para o reagendamento de um ticket específico.
 *
 * Funcionalidades:
 * - Seleção de data (com validação de data futura)
 * - Seleção de horário
 * - Escolha de setor de destino
 * - Atribuição opcional de usuário responsável
 * - Validações em tempo real
 * - Interface responsiva e profissional
 *
 * @param {boolean} open - Controla se o modal está aberto
 * @param {function} onClose - Callback executado ao fechar o modal
 * @param {function} onConfirm - Callback executado ao confirmar o reagendamento
 * @param {Object} ticket - Objeto do ticket a ser reagendado
 */

import React, { useEffect, useState } from "react";
import {
	Button,
	TextField,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Autocomplete,
	CircularProgress,
	Grid,
	Box,
	Typography,
	Divider,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { makeStyles } from 'tss-react/mui';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';

import useQueues from "../../hooks/useQueues";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles()((theme) => {
	return {
		// Dialog styling
		dialogTitle: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-start',
			gap: theme.spacing(1.5),
			backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
			borderBottom: `1px solid ${theme.palette.divider}`,
			padding: theme.spacing(2.5, 3),
			minHeight: '64px',
		},
		titleIcon: {
			fontSize: '1.5rem',
			color: theme.palette.primary.main,
		},
		titleText: {
			fontWeight: 700,
			fontSize: '1.25rem',
			color: theme.palette.text.primary,
			letterSpacing: '-0.025em',
		},

		// Content styling
		dialogContent: {
			padding: theme.spacing(3, 4),
			marginTop: '10px',
			minHeight: '420px',
			backgroundColor: theme.palette.background.default,
		},
		section: {
			marginBottom: theme.spacing(3),
		},
		sectionTitle: {
			fontWeight: 700,
			marginBottom: theme.spacing(0.5),
			color: theme.palette.text.primary,
			fontSize: '1rem',
			display: 'flex',
			alignItems: 'center',
			gap: theme.spacing(1),
			letterSpacing: '-0.015em',
		},

		// Field styling
		fieldContainer: {
			marginBottom: theme.spacing(2.5),
		},
		gridContainer: {
			marginBottom: theme.spacing(2.5),
		},
		field: {
			'& .MuiOutlinedInput-root': {
				backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : theme.palette.background.paper,
				borderRadius: theme.spacing(1.5),
				transition: 'all 0.2s ease-in-out',
				'&:hover': {
					backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : theme.palette.action.hover,
				},
				'&.Mui-focused': {
					backgroundColor: theme.palette.background.paper,
					boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
				},
				'& fieldset': {
					borderColor: theme.palette.divider,
					transition: 'border-color 0.2s ease-in-out',
				},
				'&:hover fieldset': {
					borderColor: theme.palette.text.secondary,
				},
				'&.Mui-focused fieldset': {
					borderColor: theme.palette.primary.main,
					borderWidth: '2px',
				},
			},
			'& .MuiInputLabel-root': {
				fontWeight: 600,
				fontSize: '0.9rem',
				backgroundColor: 'transparent',
				padding: '0 4px',
				'&.Mui-focused': {
					color: theme.palette.primary.main,
				},
				'&.MuiInputLabel-shrink': {
					backgroundColor: theme.palette.background.paper,
					padding: '0 8px',
				},
			},
			'& .MuiFormHelperText-root': {
				fontSize: '0.75rem',
				marginLeft: theme.spacing(0.5),
				marginTop: theme.spacing(0.5),
			},
		},

		// Actions styling
		dialogActions: {
			padding: theme.spacing(3, 4),
			backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
			borderTop: `1px solid ${theme.palette.divider}`,
			gap: theme.spacing(2),
			justifyContent: 'flex-end',
		},
		confirmButton: {
			minWidth: '160px',
			fontWeight: 700,
			borderRadius: theme.spacing(1.5),
			textTransform: 'none',
			fontSize: '0.9rem',
			padding: theme.spacing(1, 3),
			boxShadow: `0 2px 8px ${theme.palette.primary.main}30`,
			'&:hover': {
				boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
			},
		},
		cancelButton: {
			minWidth: '120px',
			borderRadius: theme.spacing(1.5),
			textTransform: 'none',
			fontSize: '0.9rem',
			fontWeight: 600,
			padding: theme.spacing(1, 2.5),
			borderColor: theme.palette.text.secondary,
			color: theme.palette.text.secondary,
			'&:hover': {
				borderColor: theme.palette.text.primary,
				color: theme.palette.text.primary,
				backgroundColor: theme.palette.action.hover,
			},
		},

		// Divider styling
		sectionDivider: {
			margin: theme.spacing(2, 0),
			backgroundColor: theme.palette.divider,
			height: '1px',
		},

		// Autocomplete specific styling
		autocompleteField: {
			'& .MuiOutlinedInput-root': {
				backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : theme.palette.background.paper,
				borderRadius: theme.spacing(1.5),
				transition: 'all 0.2s ease-in-out',
				'&:hover': {
					backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : theme.palette.action.hover,
				},
				'&.Mui-focused': {
					backgroundColor: theme.palette.background.paper,
					boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
				},
				'&.Mui-disabled': {
					backgroundColor: theme.palette.action.disabled,
					opacity: 0.6,
				},
				'& fieldset': {
					borderColor: theme.palette.divider,
					transition: 'border-color 0.2s ease-in-out',
				},
				'&:hover fieldset': {
					borderColor: theme.palette.text.secondary,
				},
				'&.Mui-focused fieldset': {
					borderColor: theme.palette.primary.main,
					borderWidth: '2px',
				},
				'&.Mui-disabled fieldset': {
					borderColor: theme.palette.action.disabled,
				},
			},
			'& .MuiInputLabel-root': {
				fontWeight: 600,
				fontSize: '0.9rem',
				backgroundColor: 'transparent',
				padding: '0 4px',
				'&.Mui-focused': {
					color: theme.palette.primary.main,
				},
				'&.MuiInputLabel-shrink': {
					backgroundColor: theme.palette.background.paper,
					padding: '0 8px',
				},
				'&.Mui-disabled': {
					color: theme.palette.text.disabled,
				},
			},
		},
	}
});

const filterOptions = createFilterOptions({
	trim: true,
});

const RescheduleTicket = ({ open, onClose, onConfirm, ticket }) => {
	// Hooks
	const { classes } = useStyles();
	const { findAll: findAllQueues } = useQueues();

	// State management
	const [options, setOptions] = useState([]);
	const [queues, setQueues] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState('');
	const [selectedDate, setSelectedDate] = useState('');
	const [selectedTime, setSelectedTime] = useState('');
	const [errors, setErrors] = useState({});

	// Effects
	useEffect(() => {
		const loadQueues = async () => {
			try {
				const list = await findAllQueues();
				setQueues(list);
			} catch (err) {
				ToastError(err);
			}
		};
		loadQueues();
	}, [findAllQueues]);

	useEffect(() => {
		if (open && ticket) {
			// Pre-populate with current ticket values
			if (ticket.queueId) {
				setSelectedQueue(ticket.queueId);
			}
		}
	}, [open, ticket]);

	useEffect(() => {
		if (!open || selectedQueue === '') {
			setLoading(false);
			return;
		}

		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchUsers = async () => {
				try {
					const { data } = await api.get(`/users-transfer/${selectedQueue}`);
					setOptions(data.users);

					// Set initial user after loading users
					if (ticket?.userId && data.users.length > 0) {
						const initialUser = data.users.find(user => user.id === ticket.userId);
						if (initialUser) {
							setSelectedUser(initialUser);
						}
					}
				} catch (err) {
					ToastError(err);
				} finally {
					setLoading(false);
				}
			};
			fetchUsers();
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [selectedQueue, open, ticket?.userId]);

	// Handlers
	const handleClose = () => {
		onClose();
		// Reset form state
		setSelectedUser(null);
		setSelectedQueue('');
		setSelectedDate('');
		setSelectedTime('');
		setErrors({});
	};

	const validateFields = () => {
		const newErrors = {};

		if (!selectedDate) {
			newErrors.date = 'Data é obrigatória';
		} else {
			const selectedDateTime = new Date(`${selectedDate}T${selectedTime || '00:00'}`);
			const now = new Date();
			if (selectedDateTime <= now) {
				newErrors.date = 'Data e hora devem ser futuras';
			}
		}

		if (!selectedTime) {
			newErrors.time = 'Horário é obrigatório';
		}

		if (!selectedQueue) {
			newErrors.queue = 'Setor é obrigatório';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleConfirm = () => {
		if (!validateFields()) {
			return;
		}

		// Format date from YYYY-MM-DD to DD-MM-YYYY
		const [year, month, day] = selectedDate.split('-');
		const formattedDate = `${day}-${month}-${year}`;

		const rescheduleData = {
			ticketId: ticket.id,
			contactId: ticket.contactId,
			rescheduleDateAt: formattedDate,
			rescheduleTimeAt: selectedTime,
			queueId: selectedQueue,
			userId: selectedUser?.id || null,
		};

		onConfirm(rescheduleData);
		handleClose();
	};

	const handleFieldChange = (field, value) => {
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors(prev => ({ ...prev, [field]: null }));
		}

		switch (field) {
			case 'date':
				setSelectedDate(value);
				break;
			case 'time':
				setSelectedTime(value);
				break;
			case 'queue':
				setSelectedQueue(value);
				setSelectedUser(null); // Reset user when queue changes
				break;
			case 'user':
				setSelectedUser(value);
				break;
			default:
				break;
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			aria-labelledby="reschedule-dialog"
			maxWidth="md"
			fullWidth
			BackdropProps={{
				style: {
					backdropFilter: 'blur(3px)',
					backgroundColor: 'rgba(0, 0, 30, 0.4)',
				},
			}}
		>
			<DialogTitle id="reschedule-dialog" className={classes.dialogTitle}>
				<ScheduleSendIcon className={classes.titleIcon} />
				<Typography variant="h6" component="div" className={classes.titleText}>
					Reagendar Atendimento #{ticket?.id}
				</Typography>
			</DialogTitle>

			<DialogContent className={classes.dialogContent}>
				<Grid container spacing={2.5}>
					{/* Seção de Data e Horário */}
					<Grid item xs={12}>
						<Typography variant="subtitle1" className={classes.sectionTitle}>
							Definir Nova Data e Horário
						</Typography>
					</Grid>

					<Grid item xs={12} sm={6}>
						<TextField
							type="date"
							label="Nova Data"
							value={selectedDate}
							onChange={(e) => handleFieldChange('date', e.target.value)}
							className={classes.field}
							variant="outlined"
							size="small"
							fullWidth
							InputLabelProps={{
								shrink: true,
							}}
							inputProps={{
								min: new Date().toISOString().split('T')[0],
							}}
							error={!!errors.date}
							helperText={errors.date || "Escolha uma data futura para o atendimento"}
							required
						/>
					</Grid>

					<Grid item xs={12} sm={6}>
						<TextField
							type="time"
							label="Novo Horário"
							value={selectedTime}
							onChange={(e) => handleFieldChange('time', e.target.value)}
							className={classes.field}
							variant="outlined"
							size="small"
							fullWidth
							InputLabelProps={{
								shrink: true,
							}}
							error={!!errors.time}
							helperText={errors.time || "Defina o horário do reagendamento"}
							required
						/>
					</Grid>

					{/* Divider */}
					<Grid item xs={12}>
						<Divider className={classes.sectionDivider} />
					</Grid>

					{/* Seção de Atribuição */}
					<Grid item xs={12}>
						<Typography variant="subtitle1" className={classes.sectionTitle}>
							Setor e Responsável
						</Typography>
					</Grid>

					<Grid item xs={12}>
						<FormControl
							variant="outlined"
							fullWidth
							size="small"
							error={!!errors.queue}
						>
							<InputLabel>Setor de Destino</InputLabel>
							<Select
								value={selectedQueue}
								onChange={(e) => handleFieldChange('queue', e.target.value)}
								label="Setor de Destino"
								className={classes.field}
							>
								<MenuItem value="">
									<em>Selecione um setor</em>
								</MenuItem>
								{queues.map((queue) => (
									<MenuItem key={queue.id} value={queue.id}>
										{queue.name}
									</MenuItem>
								))}
							</Select>
							{errors.queue && (
								<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
									{errors.queue}
								</Typography>
							)}
						</FormControl>
					</Grid>

					<Grid item xs={12}>
						<Autocomplete
							disabled={selectedQueue === ''}
							fullWidth
							size="small"
							getOptionLabel={option => `${option.name}`}
							onChange={(e, newValue) => handleFieldChange('user', newValue)}
							options={options}
							filterOptions={filterOptions}
							value={selectedUser}
							freeSolo
							autoHighlight
							noOptionsText="Nenhum usuário encontrado"
							loading={loading}
							renderInput={params => (
								<TextField
									{...params}
									size="small"
									label={selectedQueue === '' ? "Selecione um setor primeiro" : "Usuário Responsável (Opcional)"}
									variant="outlined"
									className={classes.autocompleteField}
									helperText={selectedQueue === '' ? "Escolha um setor para visualizar os usuários disponíveis" : "Deixe em branco para atribuir ao setor"}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<React.Fragment>
												{loading ? (
													<CircularProgress color="inherit" size={20} />
												) : null}
												{params.InputProps.endAdornment}
											</React.Fragment>
										),
									}}
								/>
							)}
						/>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions className={classes.dialogActions}>
				<Button
					variant="outlined"
					onClick={handleClose}
					className={classes.cancelButton}
					color="inherit"
					disabled={loading}
					size="small"
				>
					Cancelar
				</Button>
				<ButtonWithSpinner
					variant="contained"
					onClick={handleConfirm}
					color="primary"
					className={classes.confirmButton}
					loading={loading}
					disabled={!selectedDate || !selectedTime || !selectedQueue}
					startIcon={!loading && <ScheduleSendIcon />}
					size="small"
				>
					{loading ? 'Reagendando Atendimento...' : 'Confirmar Reagendamento'}
				</ButtonWithSpinner>
			</DialogActions>
		</Dialog>
	);
};

export default React.memo(RescheduleTicket);